<script lang="ts" setup>
  import ChatEmptyStateIllustration from '@/components/svg/Illustrations/ChatEmptyStateIllustration.vue'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import SegmentSwitch from '@/components/ui/segment-switch/SegmentSwitch.vue'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { useIsAiAssistantAppRoute } from '@/composables/useIsAiAssistantAppRoute'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    GLOBAL_AI_CHAT_LAST_SELECTED_SESSION_STORAGE_KEY,
    useGlobalChatStore
  } from '@/store/app/globalChat/globalChatStore'
  import type { AIChatSession, CustomerCase, SessionGroup } from '@/types/generated'
  import { AIChatSessionTypeEnum, FlagEnum } from '@/types/generated'
  import {
    FolderPlus,
    Info,
    MessageCirclePlus,
    PlusIcon,
    SearchIcon,
    Settings2
  } from 'lucide-vue-next'
  import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch, type Ref } from 'vue'
  import ChatFolderItem from '../global/components/ChatFolderItem.vue'
  import AiChatSidebarItem from './AiChatSidebarItem.vue'
  import ChatPreferencesDialog from './ChatPreferencesDialog.vue'
  import HelpLink from '@/components/app/HelpLink.vue'
  import { KNOWLEDGE_BASE_URLS } from '@/constants/knowledgeBaseUrls'
  const emit = defineEmits<{
    folderSelected: [folder: SessionGroup | null]
    folderUpdated: [folder: SessionGroup]
    createFolder: [session?: AIChatSession]
    renameFolder: [folder: SessionGroup]
    deleteChat: [session: AIChatSession]
    moveChatToFolder: [session: AIChatSession, folder: SessionGroup | null]
    switchToFoldersView: []
  }>()

  // Props to receive parent's selected folder state
  const props = defineProps<{
    selectedFolder?: SessionGroup | null
  }>()

  const { t } = useTranslation({
    en: {
      AiChat: 'AI Chat',
      GlobalAiChat: 'AI Assistant',
      Search: 'Search',
      NoMessage: 'No Chats',
      OnlyMessagesAppearHere: 'Only messages appear here',
      NewChat: 'New chat',
      Preferences: 'Preferences',
      chats: 'Chats',
      folders: 'Folders',
      allChats: 'All Chats',
      createFolder: 'Create Folder',
      deleteFolder: 'Delete Folder',
      deleteFolderConfirm: 'Are you sure you want to delete this folder?',
      deleteFolderWarning:
        'This will permanently delete the folder and all its contents, including chat sessions and associated documents.',
      deleteChatConfirm: 'Are you sure you want to delete this chat session?',
      deleteChatWarning:
        'This will permanently delete the chat session and all its associated documents.',
      cancel: 'Cancel',
      delete: 'Delete',
      noFolders: 'No folders yet',
      createFirstFolder: 'Create your first folder to organize chats',
      instructions: 'Instructions',
      workspaceInfo:
        "This workspace is separate from your specific law cases, so I can't see case details here. For that, please use the AI chat inside a specific case.",
      chatsVisible: 'The chats are visible to your law firm.',
      helpCaseAiChat: 'Learn more about the AI-Chat',
      helpGlobalAiChat: 'Learn more about the AI Assistant',
      GlobalAiChatLegacy: 'Global AI Chat',
      helpGlobalAiChatLegacy: 'Learn more about the Global-AI-Chat'
    },
    de: {
      AiChat: 'KI-Chat zur Anfrage',
      GlobalAiChat: 'KI-Assistent',
      Search: 'Suchen',
      NoMessage: 'Keine Chats',
      OnlyMessagesAppearHere: 'Hier erscheinen Ihre Chats',
      NewChat: 'Neuer Chat',
      Preferences: 'Einstellungen',
      chats: 'Chats',
      folders: 'Ordner',
      allChats: 'Alle Chats',
      createFolder: 'Ordner erstellen',
      deleteFolder: 'Ordner löschen',
      deleteFolderConfirm: 'Sind Sie sicher, dass Sie diesen Ordner löschen möchten?',
      deleteFolderWarning:
        'Dies wird den Ordner und alle Inhalte dauerhaft löschen, einschließlich Chat-Sitzungen und zugehöriger Dokumente.',
      deleteChatConfirm: 'Sind Sie sicher, dass Sie diese Chat-Sitzung löschen möchten?',
      deleteChatWarning:
        'Dies wird die Chat-Sitzung und alle zugehörigen Dokumente dauerhaft löschen.',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      noFolders: 'Noch keine Ordner',
      createFirstFolder: 'Erstellen Sie Ihren ersten Ordner, um Chats zu organisieren',
      instructions: 'Anweisungen',
      workspaceInfo:
        'Dieser Arbeitsbereich ist von Ihren spezifischen Rechtsfällen getrennt, daher kann ich hier keine Falldetails sehen. Verwenden Sie dafür den KI-Chat innerhalb eines bestimmten Falls.',
      chatsVisible: 'Die Chats sind für Ihre Kanzlei sichtbar.',
      helpCaseAiChat: 'Mehr über den KI-Chat erfahren',
      helpGlobalAiChat: 'Mehr über den KI-Assistenten erfahren',
      GlobalAiChatLegacy: 'Allgemeiner KI-Chat',
      helpGlobalAiChatLegacy: 'Mehr über den Allgemeinen KI-Chat erfahren'
    }
  })

  const floatingAiButtonFlag = useFeatureFlag(FlagEnum.FLOATING_AI_BUTTON)

  /** AI Assistant product chrome is only for ``/app/ai-assistant``, not legacy global chat. */
  const isAiAssistantAppRoute = useIsAiAssistantAppRoute()

  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))
  const sessionType = inject<AIChatSessionTypeEnum>('sessionType', AIChatSessionTypeEnum.CASE)
  const changeActiveChatSession = inject<(chatSession: AIChatSession) => void>(
    'changeActiveChatSession',
    (chatSession: AIChatSession) => {}
  )

  // Track global chat visits to show info card for first 3 visits
  const GLOBAL_CHAT_VISIT_COUNT_KEY = 'global-chat-visit-count'
  const visitCount = ref(0)

  const loadVisitCount = () => {
    try {
      const stored = localStorage.getItem(GLOBAL_CHAT_VISIT_COUNT_KEY)
      visitCount.value = stored ? parseInt(stored, 10) : 0
    } catch (error) {
      console.warn('Failed to load global chat visit count:', error)
      visitCount.value = 0
    }
  }

  const incrementVisitCount = () => {
    try {
      visitCount.value += 1
      localStorage.setItem(GLOBAL_CHAT_VISIT_COUNT_KEY, visitCount.value.toString())
    } catch (error) {
      console.warn('Failed to save global chat visit count:', error)
    }
  }

  const shouldShowInfoCard = computed(() => {
    return isGlobalContext.value && visitCount.value <= 3
  })

  // Helper function to select the first available chat session
  const selectFirstChatSession = () => {
    if (isGlobalContext.value && globalChatStore.preferEmptyGlobalFullscreenSession) {
      return
    }
    const availableChats = sortedChatSessions.value
    if (availableChats.length > 0 && !globalChatStore.activeChatSession) {
      const firstChat = availableChats[0]
      changeActiveChatSession(firstChat)
    }
  }

  // Wrapper function to clear folder selection when selecting a chat session
  const handleChatSessionSelect = (chatSession: AIChatSession) => {
    // Clear folder selection when selecting a chat session
    activeFolderId.value = null
    highlightedFolderId.value = null
    saveToStorage(STORAGE_KEYS.ACTIVE_FOLDER_ID, null)
    emit('folderSelected', null)

    // If the chat session is in a folder, open that folder and close others
    if (chatSession.session_group) {
      // Collapse ALL folders first
      collapsedFolders.value.clear()
      folders.value.forEach((folder) => {
        collapsedFolders.value.add(folder.uuid)
      })

      // Open ONLY the folder containing this chat session
      collapsedFolders.value.delete(chatSession.session_group)

      // Save the last opened folder
      saveToStorage(STORAGE_KEYS.LAST_OPENED_FOLDER, chatSession.session_group)

      // Save to localStorage
      saveCollapsedFoldersToStorage()
    } else {
      // Collapse all folders if chat is not in a folder
      collapsedFolders.value.clear()
      folders.value.forEach((folder) => {
        collapsedFolders.value.add(folder.uuid)
      })
      saveCollapsedFoldersToStorage()

      // Clear last opened folder if chat is not in a folder
      saveToStorage(STORAGE_KEYS.LAST_OPENED_FOLDER, null)
    }

    // Save the last selected chat session
    saveToStorage(STORAGE_KEYS.LAST_SELECTED_CHAT, chatSession.uuid)

    // Set the active chat session
    changeActiveChatSession(chatSession)
  }
  const createNewChatSession = inject<(sessionGroupUuid?: string) => Promise<AIChatSession | null>>(
    'createNewChatSession',
    async () => null
  )

  const globalChatStore = useGlobalChatStore()

  const isGlobalContext = computed(() => sessionType === AIChatSessionTypeEnum.GLOBAL)
  const isCaseContext = computed(() => sessionType === AIChatSessionTypeEnum.CASE)

  watch(
    isGlobalContext,
    (global) => {
      if (global) {
        void floatingAiButtonFlag.checkFlag()
      }
    },
    { immediate: true }
  )

  // Folder management state
  const activeSegment = ref('chats')
  const searchQuery = ref('')
  const activeFolderId = ref<string | null>(null)
  const isInitialMount = ref(true)

  // Helper function to extract text content from message
  const getMessageContent = (msg: any): string => {
    // Handle string content (user messages)
    if (typeof msg.content === 'string') {
      return msg.content
    }

    // Handle assistant messages with chunks
    if (msg.content && typeof msg.content === 'object' && 'chunks' in msg.content) {
      const chunks = msg.content.chunks
      if (Array.isArray(chunks)) {
        const textChunks = chunks
          .filter((chunk) => chunk && typeof chunk === 'object' && chunk.type === 'text')
          .map((chunk) => {
            const text = chunk.text
            return typeof text === 'string' ? text : ''
          })
          .filter((text) => text.trim().length > 0) // Filter out empty strings
        return textChunks.join(' ').trim() // Join with spaces and trim
      }
    }

    // Handle messages with json method (complex message chunks)
    if (msg.content && typeof msg.content === 'object' && 'json' in msg.content) {
      try {
        return (msg.content as { json: () => string }).json()
      } catch {
        return ''
      }
    }

    // Fallback
    return String(msg.content || '').trim()
  }

  const highlightedFolderId = ref<string | null>(null)
  const collapsedFolders = ref<Set<string>>(new Set())

  // Delete confirmation dialogs
  const showDeleteFolderDialog = ref(false)
  const showDeleteChatDialog = ref(false)
  const folderToDelete = ref<SessionGroup | null>(null)
  const chatToDelete = ref<AIChatSession | null>(null)

  // Ref for scrolling to active chat session
  const activeChatSessionRef = ref<HTMLElement | null>(null)

  // localStorage keys
  const STORAGE_KEYS = {
    ACTIVE_SEGMENT: 'ai-chat-active-segment',
    ACTIVE_FOLDER_ID: 'ai-chat-active-folder-id',
    COLLAPSED_FOLDERS: 'ai-chat-collapsed-folders',
    LAST_OPENED_FOLDER: 'ai-chat-last-opened-folder',
    LAST_SELECTED_CHAT: GLOBAL_AI_CHAT_LAST_SELECTED_SESSION_STORAGE_KEY
  }

  // User filter functionality
  const selectedUserFilter = ref<string | null>(null)
  const showUserDropdown = ref(false)

  const sortedChatSessions = computed(() => {
    if (isCaseContext.value && caseData && caseData.value && caseData.value.chat_sessions) {
      let sessions = [...caseData.value.chat_sessions]

      // Filter by search query for case chat sessions
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.trim().toLowerCase()
        sessions = sessions.filter((session) => {
          if (!query) return true

          // Search ONLY in message content, not title or user
          if (!session.messages || session.messages.length === 0) {
            return false
          }

          // Check messages - EXCLUDE system messages from search
          const hasMessageMatch = session.messages.some((msg) => {
            // Skip system messages - they contain prompts/instructions, not user content
            if (msg.role === 'system') {
              return false
            }

            const content = getMessageContent(msg)
            // Ensure content is a non-empty string before searching
            if (!content || typeof content !== 'string' || content.trim().length === 0) {
              return false
            }
            return content.toLowerCase().includes(query)
          })

          return hasMessageMatch
        })
      }

      return sessions.sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })
    }

    if (isGlobalContext.value) {
      let sessions = [...globalChatStore.chatSessions]

      // Only filter by active folder when in "Folders" segment and a folder is selected
      if (activeSegment.value === 'folders' && activeFolderId.value) {
        sessions = sessions.filter((session) => session.session_group === activeFolderId.value)
      }
      // When in "Chats" segment, show ALL chat sessions (both standalone and folder chats)

      // Filter by search query
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.trim().toLowerCase()
        sessions = sessions.filter((session) => {
          if (!query) return true

          // Search ONLY in message content, not title or user
          if (!session.messages || session.messages.length === 0) {
            return false
          }

          // Check messages - EXCLUDE system messages from search
          const hasMessageMatch = session.messages.some((msg) => {
            // Skip system messages - they contain prompts/instructions, not user content
            if (msg.role === 'system') {
              return false
            }

            const content = getMessageContent(msg)
            // Ensure content is a non-empty string before searching
            if (!content || typeof content !== 'string' || content.trim().length === 0) {
              return false
            }
            return content.toLowerCase().includes(query)
          })

          return hasMessageMatch
        })
      }

      return sessions.sort((a, b) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })
    }

    return []
  })

  const chatTitle = computed(() => {
    if (!isGlobalContext.value) {
      return t('AiChat')
    }
    if (isAiAssistantAppRoute.value && floatingAiButtonFlag.isEnabled.value) {
      return t('GlobalAiChat')
    }
    return t('GlobalAiChatLegacy')
  })

  const globalHelpTooltip = computed(() =>
    isAiAssistantAppRoute.value && floatingAiButtonFlag.isEnabled.value
      ? t('helpGlobalAiChat')
      : t('helpGlobalAiChatLegacy')
  )

  // Folder management computed properties
  const folders = computed(() => globalChatStore.sessionGroups)
  const hasFolders = computed(() => folders.value.length > 0)
  const segmentOptions = computed(() => [
    { value: 'chats', label: t('chats') },
    { value: 'folders', label: t('folders') }
  ])

  const getFolderChatCount = (folderId: string) => {
    let sessions = globalChatStore.chatSessions.filter(
      (session) => session.session_group === folderId
    )

    // Apply search filter if there's a search query
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.trim().toLowerCase()
      sessions = sessions.filter((session) => {
        if (!query) return true

        // Search ONLY in message content, not title or user
        if (!session.messages || session.messages.length === 0) {
          return false
        }

        // Check messages - EXCLUDE system messages from search
        const hasMessageMatch = session.messages.some((msg) => {
          // Skip system messages - they contain prompts/instructions, not user content
          if (msg.role === 'system') {
            return false
          }

          const content = getMessageContent(msg)
          // Ensure content is a non-empty string before searching
          if (!content || typeof content !== 'string' || content.trim().length === 0) {
            return false
          }
          return content.toLowerCase().includes(query)
        })

        return hasMessageMatch
      })
    }

    return sessions.length
  }

  const getFolderChatSessions = (folderId: string) => {
    let sessions = globalChatStore.chatSessions.filter(
      (session) => session.session_group === folderId
    )

    // Apply search filter if there's a search query
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.trim().toLowerCase()
      sessions = sessions.filter((session) => {
        if (!query) return true

        // Search ONLY in message content, not title or user
        if (!session.messages || session.messages.length === 0) {
          return false
        }

        // Check messages - EXCLUDE system messages from search
        const hasMessageMatch = session.messages.some((msg) => {
          // Skip system messages - they contain prompts/instructions, not user content
          if (msg.role === 'system') {
            return false
          }

          const content = getMessageContent(msg)
          // Ensure content is a non-empty string before searching
          if (!content || typeof content !== 'string' || content.trim().length === 0) {
            return false
          }
          return content.toLowerCase().includes(query)
        })

        return hasMessageMatch
      })
    }

    return sessions.sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
  }

  // Preferences functionality
  const showPreferencesDialog = ref(false)
  const openPreferencesDialog = () => {
    showPreferencesDialog.value = true
    // Remove focus from the button to prevent it staying focused when dialog closes
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }

  // Folder management methods
  const selectFolder = (folderId: string | null) => {
    activeFolderId.value = folderId
    highlightedFolderId.value = folderId
    // Clear active chat session when selecting a folder
    globalChatStore.setActiveChatSession(null)

    // Clear last selected chat when selecting a folder
    saveToStorage(STORAGE_KEYS.LAST_SELECTED_CHAT, null)

    // Auto-open selected folder and close others
    if (folderId) {
      // Collapse ALL folders first
      collapsedFolders.value.clear()
      folders.value.forEach((folder) => {
        collapsedFolders.value.add(folder.uuid)
      })

      // Open ONLY the selected folder
      collapsedFolders.value.delete(folderId)

      // Save the last opened folder
      saveToStorage(STORAGE_KEYS.LAST_OPENED_FOLDER, folderId)

      // Save to localStorage
      saveCollapsedFoldersToStorage()
    } else {
      // Collapse all folders if no folder selected
      collapsedFolders.value.clear()
      folders.value.forEach((folder) => {
        collapsedFolders.value.add(folder.uuid)
      })
      saveCollapsedFoldersToStorage()

      // Clear last opened folder if no folder selected
      saveToStorage(STORAGE_KEYS.LAST_OPENED_FOLDER, null)
    }

    // Find the folder object and emit it
    const folder = folderId
      ? globalChatStore.sessionGroups.find((f) => f.uuid === folderId) || null
      : null
    emit('folderSelected', folder)
  }

  const createFolder = (session?: AIChatSession) => {
    emit('createFolder', session)
  }

  const renameFolder = (folder: SessionGroup) => {
    // Call parent's renameFolder method
    emit('renameFolder', folder)
  }

  const deleteChat = (session: AIChatSession) => {
    chatToDelete.value = session
    showDeleteChatDialog.value = true
  }

  const confirmDeleteChat = async () => {
    if (!chatToDelete.value) return

    try {
      await globalChatStore.deleteChatSession(chatToDelete.value.uuid)
      // Emit to parent to handle any additional cleanup
      emit('deleteChat', chatToDelete.value)
    } catch (error) {
      console.error('Failed to delete chat session:', error)
    } finally {
      showDeleteChatDialog.value = false
      chatToDelete.value = null
    }
  }

  const deleteFolder = (folder: SessionGroup) => {
    folderToDelete.value = folder
    showDeleteFolderDialog.value = true
  }

  const confirmDeleteFolder = async () => {
    if (!folderToDelete.value) return

    try {
      await globalChatStore.deleteSessionGroup(folderToDelete.value.uuid)
      if (activeFolderId.value === folderToDelete.value.uuid) {
        activeFolderId.value = null
        saveToStorage(STORAGE_KEYS.ACTIVE_FOLDER_ID, null)
      }
    } catch (error) {
      console.error('Failed to delete folder:', error)
    } finally {
      showDeleteFolderDialog.value = false
      folderToDelete.value = null
    }
  }

  const handleMoveChatToFolder = (session: AIChatSession, folder: SessionGroup | null) => {
    // Emit the event to parent components
    emit('moveChatToFolder', session, folder)
  }

  const handleMoveToFolderFromSidebarItem = (
    session: AIChatSession,
    folder: SessionGroup | null
  ) => {
    // Move the chat to the folder
    emit('moveChatToFolder', session, folder)

    if (folder) {
      // If moving to a folder, switch to folders view and select the moved chat
      activeSegment.value = 'folders'
      // Set activeFolderId for filtering (to show the folder's chats)
      activeFolderId.value = folder.uuid
      // Don't set highlightedFolderId - we don't want to highlight the folder
      // highlightedFolderId.value = folder.uuid
      emit('switchToFoldersView')
      // Don't emit folderSelected - we want to select the chat, not the folder
      // emit('folderSelected', folder)
      // Select the moved chat session
      changeActiveChatSession(session)
    } else {
      // If moving to "No Folder", switch to chats view and clear folder selection
      activeSegment.value = 'chats'
      activeFolderId.value = null
      highlightedFolderId.value = null
      saveToStorage(STORAGE_KEYS.ACTIVE_FOLDER_ID, null) // Ensure localStorage is updated
      emit('folderSelected', null)
      // Select the moved chat session
      changeActiveChatSession(session)
    }
  }

  const handleCreateFolderFromSidebarItem = (session: AIChatSession) => {
    console.log('handleCreateFolderFromSidebarItem called with session:', session)
    console.log('session type:', typeof session)
    console.log('session uuid:', session?.uuid)

    // Switch to folders view first
    activeSegment.value = 'folders'
    emit('switchToFoldersView')

    // Emit create folder event with the session
    emit('createFolder', session)
  }

  // Click outside handler for user dropdown
  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement
    if (!target.closest('.user-dropdown-container')) {
      showUserDropdown.value = false
    }
  }

  // Watch for segment changes to clear folder selection when switching to "Chats"
  watch(activeSegment, (newSegment) => {
    // Save to localStorage
    saveToStorage(STORAGE_KEYS.ACTIVE_SEGMENT, newSegment)

    if (newSegment === 'chats') {
      activeFolderId.value = null
      saveToStorage(STORAGE_KEYS.ACTIVE_FOLDER_ID, null)
      emit('folderSelected', null)
      // Clear search when switching to chats view
      searchQuery.value = ''
    } else if (newSegment === 'folders') {
      // Don't auto-select folder when switching - only on mount
    }
  })

  // Watch for folder selection changes
  watch(activeFolderId, (newFolderId) => {
    saveToStorage(STORAGE_KEYS.ACTIVE_FOLDER_ID, newFolderId)
    // Clear search when no folder is selected
    if (!newFolderId) {
      searchQuery.value = ''
    }
  })

  // Collapsed folders persistence functions - defined before watchers that use them
  const loadCollapsedFoldersFromStorage = () => {
    try {
      // Get the last opened folder
      const lastOpenedFolder = loadFromStorage(STORAGE_KEYS.LAST_OPENED_FOLDER, '')

      // Start with ALL folders collapsed
      collapsedFolders.value = new Set()

      // If there's a last opened folder, open ONLY that one
      if (lastOpenedFolder && lastOpenedFolder !== '') {
        // Add all folders to collapsed set first
        folders.value.forEach((folder) => {
          collapsedFolders.value.add(folder.uuid)
        })
        // Then remove the last opened folder (making it open)
        collapsedFolders.value.delete(lastOpenedFolder)
      } else {
        // If no last opened folder, keep all folders collapsed
        folders.value.forEach((folder) => {
          collapsedFolders.value.add(folder.uuid)
        })
      }
    } catch (error) {
      console.warn('Failed to load collapsed folders from localStorage:', error)
      // Start with all folders collapsed on error
      collapsedFolders.value = new Set()
      folders.value.forEach((folder) => {
        collapsedFolders.value.add(folder.uuid)
      })
    }
  }

  // Watch for parent's selectedFolder changes to sync sidebar state
  watch(
    () => props.selectedFolder,
    (newSelectedFolder) => {
      if (newSelectedFolder === null) {
        // Parent cleared folder selection, clear sidebar state
        activeFolderId.value = null
        highlightedFolderId.value = null
        saveToStorage(STORAGE_KEYS.ACTIVE_FOLDER_ID, null)
      }
    }
  )

  // Watch for folders to be loaded and initialize collapsed state
  watch(
    () => folders.value,
    (newFolders) => {
      // Only initialize collapsed state if folders are available and we haven't initialized yet
      if (newFolders.length > 0 && collapsedFolders.value.size === 0) {
        loadCollapsedFoldersFromStorage()
      }
    },
    { immediate: true }
  )

  // Watch for chat sessions to be loaded and restore last selected chat
  watch(
    [() => globalChatStore.chatSessions, () => caseData?.value?.chat_sessions],
    () => {
      if (isGlobalContext.value && globalChatStore.preferEmptyGlobalFullscreenSession) {
        return
      }
      // Only restore if we have a last selected chat and no current active session
      const lastSelectedChatId = loadFromStorage(STORAGE_KEYS.LAST_SELECTED_CHAT, '')
      if (lastSelectedChatId && !globalChatStore.activeChatSession) {
        // Find the chat session in the appropriate context
        let chatToRestore: AIChatSession | null = null

        if (isCaseContext.value && caseData?.value?.chat_sessions) {
          chatToRestore =
            caseData.value.chat_sessions.find((session) => session.uuid === lastSelectedChatId) ||
            null
        } else if (isGlobalContext.value) {
          chatToRestore =
            globalChatStore.chatSessions.find((session) => session.uuid === lastSelectedChatId) ||
            null
        }

        if (chatToRestore) {
          // Restore the chat session
          changeActiveChatSession(chatToRestore)

          // If it's in a folder, open that folder
          if (chatToRestore.session_group) {
            // Collapse all folders first
            collapsedFolders.value.clear()
            folders.value.forEach((folder) => {
              collapsedFolders.value.add(folder.uuid)
            })
            // Open the folder containing this chat
            collapsedFolders.value.delete(chatToRestore.session_group)
            saveCollapsedFoldersToStorage()
          }
        } else {
          // If chat session not found in current context, clear the stored ID
          saveToStorage(STORAGE_KEYS.LAST_SELECTED_CHAT, null)
        }
      }
    },
    { immediate: false }
  )

  // Ref to the scrollable container
  const scrollableContainerRef = ref<HTMLElement | null>(null)

  // Method to scroll to active chat session
  const scrollToActiveChatSession = () => {
    // Find the active chat session element by UUID
    const activeSessionUuid = globalChatStore.activeChatSession?.uuid
    console.log('🔍 [Scroll] Attempting to scroll to session:', activeSessionUuid)

    if (activeSessionUuid && scrollableContainerRef.value) {
      // Try to find the element within the scrollable container
      const element = scrollableContainerRef.value.querySelector(
        `[data-session-uuid="${activeSessionUuid}"]`
      ) as HTMLElement | null
      console.log('🔍 [Scroll] Found element:', element)

      if (element && scrollableContainerRef.value) {
        console.log('🔍 [Scroll] Scrolling to element within sidebar container')
        const container = scrollableContainerRef.value

        // Get positions relative to viewport
        const elementRect = element.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()

        // Calculate element position relative to container
        const elementTopRelative = elementRect.top - containerRect.top + container.scrollTop
        const elementHeight = elementRect.height
        const containerHeight = container.clientHeight
        const containerScrollTop = container.scrollTop

        // Calculate the position to scroll to (center the element in the container)
        const scrollTo = elementTopRelative - containerHeight / 2 + elementHeight / 2

        // Only scroll if element is not already visible
        const isElementVisible =
          elementTopRelative >= containerScrollTop &&
          elementTopRelative + elementHeight <= containerScrollTop + containerHeight

        if (!isElementVisible) {
          container.scrollTo({
            top: Math.max(0, scrollTo),
            behavior: 'smooth'
          })
        }
      } else {
        console.log('🔍 [Scroll] Element not found for UUID:', activeSessionUuid)
      }
    }
  }

  // Watch for active chat session changes to scroll to it
  watch(
    () => globalChatStore.activeChatSession?.uuid,
    (newUuid, oldUuid) => {
      if (newUuid && newUuid !== oldUuid) {
        // Use nextTick and a small delay to ensure DOM is fully updated
        nextTick(() => {
          setTimeout(() => {
            scrollToActiveChatSession()
          }, 100)
        })
      }
    }
  )

  // Watch for folder selection changes from external sources (like FolderView)
  watch(
    () => globalChatStore.activeChatSession?.uuid,
    (newUuid) => {
      // If a chat session is selected, clear folder selection
      if (newUuid && activeFolderId.value) {
        activeFolderId.value = null
        saveToStorage(STORAGE_KEYS.ACTIVE_FOLDER_ID, null)
      }
    }
  )

  // localStorage functions
  const saveToStorage = (key: string, value: string | null) => {
    try {
      if (value === null) {
        localStorage.removeItem(key)
      } else {
        localStorage.setItem(key, value)
      }
    } catch (error) {
      console.warn('Failed to save to localStorage:', error)
    }
  }

  const loadFromStorage = (key: string, defaultValue: string): string => {
    try {
      return localStorage.getItem(key) || defaultValue
    } catch (error) {
      console.warn('Failed to load from localStorage:', error)
      return defaultValue
    }
  }

  // Collapsed folders persistence functions
  const saveCollapsedFoldersToStorage = () => {
    try {
      const collapsedArray = Array.from(collapsedFolders.value)
      localStorage.setItem(STORAGE_KEYS.COLLAPSED_FOLDERS, JSON.stringify(collapsedArray))
    } catch (error) {
      console.warn('Failed to save collapsed folders to localStorage:', error)
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)

    // Track visit count for global chat
    if (isGlobalContext.value) {
      loadVisitCount()
      incrementVisitCount()
    }

    // Load saved preferences from localStorage
    const savedSegment = loadFromStorage(STORAGE_KEYS.ACTIVE_SEGMENT, 'chats')
    const savedFolderId = loadFromStorage(STORAGE_KEYS.ACTIVE_FOLDER_ID, '')

    // Validate saved segment
    if (savedSegment === 'chats' || savedSegment === 'folders') {
      activeSegment.value = savedSegment
    }

    if (isGlobalContext.value && globalChatStore.preferEmptyGlobalFullscreenSession) {
      activeSegment.value = 'chats'
      activeFolderId.value = null
      highlightedFolderId.value = null
      saveToStorage(STORAGE_KEYS.ACTIVE_SEGMENT, 'chats')
      saveToStorage(STORAGE_KEYS.ACTIVE_FOLDER_ID, null)
      collapsedFolders.value.clear()
      folders.value.forEach((folder) => {
        collapsedFolders.value.add(folder.uuid)
      })
      saveCollapsedFoldersToStorage()
      emit('folderSelected', null)
      isInitialMount.value = false
      return
    }

    // Check if there's an active chat session and highlight it appropriately
    const activeSession = globalChatStore.activeChatSession
    if (activeSession) {
      // If the active session is in a folder, switch to folders view and highlight the folder
      if (activeSession.session_group) {
        const sessionFolder = folders.value.find((f) => f.uuid === activeSession.session_group)
        if (sessionFolder) {
          activeSegment.value = 'folders'
          activeFolderId.value = sessionFolder.uuid
          highlightedFolderId.value = sessionFolder.uuid

          // Open this folder and close others
          collapsedFolders.value.clear()
          folders.value.forEach((folder) => {
            collapsedFolders.value.add(folder.uuid)
          })
          collapsedFolders.value.delete(sessionFolder.uuid)
          saveCollapsedFoldersToStorage()

          // Intentionally do NOT emit ``folderSelected`` here. This branch is
          // purely cosmetic — it highlights the folder that contains the
          // already-active session in the sidebar's local UI state. The
          // parent's ``handleFolderSelected`` clears
          // ``globalChatStore.activeChatSession`` whenever it receives a truthy
          // folder, because user-driven navigation via :func:`selectFolder`
          // legitimately drops the open thread to show the folder landing
          // view. Emitting from this cosmetic mount path would clobber the
          // session that side-panel "expand to fullscreen" just preloaded,
          // dumping the user on the folder view instead of the chat they just
          // expanded. The genuine "user picked a folder" path already clears
          // the active session inside :func:`selectFolder` before emitting, so
          // suppressing the cosmetic emit fixes the regression without
          // affecting any other path.
        }
      } else {
        // If the active session is standalone, switch to chats view
        activeSegment.value = 'chats'
        activeFolderId.value = null
        highlightedFolderId.value = null
        saveToStorage(STORAGE_KEYS.ACTIVE_FOLDER_ID, null)
        emit('folderSelected', null)
      }
    } else {
      // No active session, use saved preferences
      if (savedSegment === 'folders' && folders.value.length > 0) {
        // Only auto-select first folder on initial mount
        if (isInitialMount.value) {
          const firstFolder = folders.value[0]
          selectFolder(firstFolder.uuid)
        }
      } else if (savedSegment === 'chats') {
        // Only auto-select first chat session on initial mount
        if (isInitialMount.value) {
          selectFirstChatSession()
        }
      } else {
        // For other segments, validate saved folder ID (check if folder still exists)
        if (savedFolderId && folders.value.some((folder) => folder.uuid === savedFolderId)) {
          activeFolderId.value = savedFolderId
          highlightedFolderId.value = savedFolderId

          // Open this folder and close others
          collapsedFolders.value.clear()
          folders.value.forEach((folder) => {
            collapsedFolders.value.add(folder.uuid)
          })
          collapsedFolders.value.delete(savedFolderId)
          saveCollapsedFoldersToStorage()

          // Emit the selected folder
          const folder = folders.value.find((f) => f.uuid === savedFolderId)
          if (folder) {
            emit('folderSelected', folder)
          }
        } else if (isInitialMount.value) {
          // Default to chats segment and select first chat if available (only on initial mount)
          activeSegment.value = 'chats'
          selectFirstChatSession()
        }
      }
    }

    // Mark that initial mount is complete
    isInitialMount.value = false
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })

  // Expose methods for parent component
  defineExpose({
    openPreferencesDialog,
    selectFolder
  })
</script>
<template>
  <div
    class="w-72 flex-none"
    data-cy="chat-sidebar"
  >
    <div class="flex h-full">
      <!-- Chat Threads -->
      <div
        class="size-full truncate rounded-s-lg border-x border-gray-200 bg-white"
        data-cy="sidebar-content"
      >
        <div class="flex h-full flex-col">
          <!-- Header -->
          <div
            class="flex items-center justify-between gap-x-2 px-5 py-3"
            data-cy="sidebar-header"
          >
            <div class="flex items-center gap-2">
              <span
                id="hs-pro-chat-sidebar-label"
                class="block truncate text-lg font-semibold text-gray-800"
                data-cy="sidebar-title"
              >
                {{ chatTitle }}
              </span>
              <HelpLink
                v-if="!isGlobalContext"
                :url="KNOWLEDGE_BASE_URLS.caseAiChat"
                :tooltip="t('helpCaseAiChat')"
                location="case_ai_chat"
              />
              <HelpLink
                v-if="isGlobalContext"
                :url="KNOWLEDGE_BASE_URLS.globalAiChat"
                :tooltip="globalHelpTooltip"
                location="global_ai_chat"
              />
            </div>

            <div
              class="flex items-center"
              data-cy="sidebar-actions"
            >
              <!-- Select -->
              <div class="relative inline-block">
                <div class="absolute end-2 top-1/2 -translate-y-1/2"></div>
              </div>
              <!-- End Select -->

              <div
                class="relative ms-2 flex items-center gap-x-1 ps-2 before:absolute before:start-0 before:top-1/2
                  before:h-4 before:w-px before:-translate-y-1/2 before:bg-gray-200"
              >
                <!-- New Chat Button -->
                <button
                  type="button"
                  class="flex size-8 items-center justify-center gap-x-3 rounded-full text-sm text-gray-600 hover:bg-gray-100
                    focus:bg-gray-100 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
                  data-cy="new-chat-button"
                  @click="() => createNewChatSession(activeFolderId || undefined)"
                >
                  <MessageCirclePlus class="size-4 shrink-0 text-gray-600" />
                  <span class="sr-only">{{ t('NewChat') }}</span>
                </button>
                <!-- End New Chat Button -->
              </div>
            </div>
          </div>
          <!-- End Header -->

          <!-- Global Context: Informational Bubble (shown for first 3 visits) -->
          <div
            v-if="shouldShowInfoCard"
            class="px-4 pb-3 pt-0"
            data-cy="info-card"
          >
            <div
              class="whitespace-normal break-words rounded-lg bg-blue-50 p-3 text-xs leading-relaxed text-blue-800"
            >
              {{ t('workspaceInfo') }}
            </div>
          </div>

          <!-- Global Context: Segment Switch -->
          <div
            v-if="isGlobalContext"
            class="px-4 pb-3"
            data-cy="segment-switch"
          >
            <SegmentSwitch
              v-model:value="activeSegment"
              :options="segmentOptions"
            />
          </div>

          <!-- Global Context: New Chat/New Folder Button -->
          <div
            v-if="isGlobalContext"
            class="px-4 pb-3"
          >
            <button
              v-if="activeSegment === 'chats'"
              type="button"
              class="relative size-full rounded-md bg-white"
              data-cy="new-chat-button"
              @click="() => createNewChatSession()"
            >
              <div
                class="relative box-border flex size-full content-stretch items-center justify-center overflow-clip p-1.5"
              >
                <div class="relative size-5 shrink-0 overflow-clip">
                  <MessageCirclePlus class="h-5 w-5 text-gray-800" />
                </div>
                <div
                  class="relative box-border flex shrink-0 content-stretch items-center justify-center gap-1.5 px-1.5 py-0"
                >
                  <div
                    class="leading-0 relative shrink-0 text-nowrap text-sm font-medium not-italic text-gray-800"
                  >
                    <p class="whitespace-pre leading-5">{{ t('NewChat') }}</p>
                  </div>
                </div>
              </div>
              <div
                aria-hidden="true"
                class="pointer-events-none absolute inset-0 rounded-md border border-solid border-gray-200
                  shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
              />
            </button>
            <button
              v-else
              type="button"
              class="relative size-full rounded-md bg-white"
              data-cy="create-folder-button"
              @click="() => createFolder()"
            >
              <div
                class="relative box-border flex size-full content-stretch items-center justify-center overflow-clip p-1.5"
              >
                <div class="relative size-5 shrink-0 overflow-clip">
                  <FolderPlus class="h-5 w-5 text-gray-800" />
                </div>
                <div
                  class="relative box-border flex shrink-0 content-stretch items-center justify-center gap-1.5 px-1.5 py-0"
                >
                  <div
                    class="leading-0 relative shrink-0 text-nowrap text-sm font-medium not-italic text-gray-800"
                  >
                    <p class="whitespace-pre leading-5">{{ t('createFolder') }}</p>
                  </div>
                </div>
              </div>
              <div
                aria-hidden="true"
                class="pointer-events-none absolute inset-0 rounded-md border border-solid border-gray-200
                  shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
              />
            </button>
          </div>

          <!-- Search -->
          <div class="px-4 pb-3">
            <!-- SearchBox -->
            <div
              class="flex items-center gap-1 rounded-[6px] border border-gray-200 bg-white px-1 py-0 shadow-sm"
              data-cy="search-box"
            >
              <SearchIcon class="size-5 shrink-0 text-gray-400" />
              <input
                v-model="searchQuery"
                type="text"
                class="flex-1 border-0 bg-transparent py-2 text-sm leading-4 placeholder:text-gray-400 focus:outline-none
                  focus:ring-0 disabled:pointer-events-none disabled:opacity-50"
                role="combobox"
                aria-expanded="false"
                :placeholder="`${t('Search')}`"
                data-hs-combo-box-input
                data-cy="search-input"
              />
            </div>
            <!-- User Filter Dropdown -->
            <!-- <div class="user-dropdown-container relative">
                <button
                  type="button"
                  class="flex items-center gap-1 border-l border-gray-200 px-3 py-1.5 text-gray-400 hover:text-gray-600
                    focus:outline-none"
                  @click="showUserDropdown = !showUserDropdown"
                >
                  <User class="size-4 shrink-0" />
                  <ChevronDown class="size-3 shrink-0" />
                </button>
                <div
                  v-if="showUserDropdown"
                  class="absolute right-0 top-full z-30 mt-1 w-48 rounded-md border border-gray-200 bg-white shadow-lg"
                >
                  <div class="py-1">
                    <button
                      type="button"
                      class="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      :class="{ 'bg-gray-100': selectedUserFilter === null }"
                      @click="
                        () => {
                          selectedUserFilter = null
                          showUserDropdown = false
                        }
                      "
                    >
                      All Users
                    </button>
                  </div>
                </div>
              </div> -->
            <!-- End Searchbox -->
          </div>
          <!-- End Search -->

          <div
            ref="scrollableContainerRef"
            class="h-full overflow-y-auto"
          >
            <!-- Tab Content -->
            <div class="h-full">
              <!-- Global Context: Folders View -->
              <template v-if="isGlobalContext && activeSegment === 'folders'">
                <template v-if="hasFolders">
                  <div
                    class="space-y-1"
                    data-cy="folders-list"
                  >
                    <ChatFolderItem
                      v-for="folder in folders"
                      :key="folder.uuid"
                      :folder="folder"
                      :active="highlightedFolderId === folder.uuid"
                      :chat-count="getFolderChatCount(folder.uuid)"
                      :chat-sessions="getFolderChatSessions(folder.uuid)"
                      :active-chat-session-id="globalChatStore.activeChatSession?.uuid"
                      :active-chat-session-ref="activeChatSessionRef"
                      :collapsed="collapsedFolders.has(folder.uuid)"
                      @select="(folder: SessionGroup | null) => selectFolder(folder?.uuid || null)"
                      @rename="renameFolder"
                      @delete="deleteFolder"
                      @select-chat="handleChatSessionSelect"
                      @delete-chat="deleteChat"
                      @move-chat-to-folder="handleMoveChatToFolder"
                      @create-folder="createFolder"
                      @move-to-folder-from-sidebar-item="handleMoveToFolderFromSidebarItem"
                      @create-folder-from-sidebar-item="handleCreateFolderFromSidebarItem"
                    />
                  </div>
                </template>
                <!-- Empty State for Folders -->
                <div
                  v-else
                  class="flex h-full flex-col items-center justify-center p-5 text-center"
                  data-cy="folders-empty-state"
                >
                  <div
                    class="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-200"
                  >
                    <PlusIcon class="h-6 w-6 text-gray-400" />
                  </div>
                  <p class="mb-2 text-sm text-gray-500">{{ t('noFolders') }}</p>
                  <p class="whitespace-normal break-words text-xs text-gray-400">
                    {{ t('createFirstFolder') }}
                  </p>
                </div>
              </template>

              <!-- Chats View (Case Context or Global Context) -->
              <template v-else-if="sortedChatSessions.length > 0">
                <AiChatSidebarItem
                  v-for="session in sortedChatSessions"
                  :key="session.uuid"
                  :data-session-uuid="session.uuid"
                  :chatSession="session"
                  @click="handleChatSessionSelect(session)"
                  @select="handleChatSessionSelect"
                  @delete="deleteChat"
                  @move-to-folder="handleMoveToFolderFromSidebarItem"
                  @create-folder="handleCreateFolderFromSidebarItem"
                />
              </template>
              <!-- Empty State -->
              <div
                v-else
                class="flex h-full flex-col items-center justify-center p-5 text-center"
                data-cy="chats-empty-state"
              >
                <ChatEmptyStateIllustration />

                <div class="mx-auto max-w-sm text-wrap">
                  <p class="mt-2 font-medium text-gray-800">{{ t('NoMessage') }}</p>
                  <p class="mb-5 text-sm text-gray-500">{{ t('OnlyMessagesAppearHere') }}</p>
                </div>
              </div>
              <!-- Empty State -->
            </div>
          </div>

          <!-- Bottom Actions -->
          <div class="border-t border-gray-200 p-4">
            <!-- Global Context: Info and Actions -->
            <div v-if="isGlobalContext">
              <div class="mb-4 flex items-center gap-2 rounded-lg bg-blue-50 p-3">
                <Info class="size-4 shrink-0 text-blue-600" />
                <p class="whitespace-normal break-words text-xs leading-relaxed text-blue-800">
                  {{ t('chatsVisible') }}
                </p>
              </div>
              <div class="flex gap-2">
                <button
                  type="button"
                  class="flex flex-1 items-center justify-center gap-x-2 rounded-md border border-gray-300 px-3 py-2 text-sm
                    font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
                    focus:ring-offset-2"
                  data-cy="preferences-button"
                  @click="openPreferencesDialog"
                >
                  <Settings2 class="size-4 shrink-0" />
                  {{ t('Preferences') }}
                </button>
                <!-- <button
                  type="button"
                  class="flex flex-1 items-center justify-center gap-x-2 rounded-md border border-gray-300 px-3 py-2 text-sm
                    font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
                    focus:ring-offset-2"
                  @click="handleInstructions"
                >
                  <BookOpen class="size-4 shrink-0" />
                  {{ t('instructions') }}
                </button> -->
              </div>
            </div>

            <!-- Case Context: Preferences Only -->
            <div v-else>
              <button
                type="button"
                class="flex w-full items-center justify-center gap-x-2 rounded-md border border-gray-300 px-3 py-2 text-sm
                  font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
                  focus:ring-offset-2"
                data-cy="preferences-button"
                @click="openPreferencesDialog"
              >
                <Settings2 class="size-4 shrink-0" />
                {{ t('Preferences') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- End Chat Threads -->
    </div>

    <!-- Chat Memories Dialog has been moved to AiChatMain.vue -->
  </div>

  <!-- Chat Preferences Dialog -->
  <Teleport to="body">
    <ChatPreferencesDialog
      v-if="showPreferencesDialog"
      source="AI_CHAT"
      @close="showPreferencesDialog = false"
    />
  </Teleport>

  <!-- Delete Folder Confirmation Dialog -->
  <AlertDialog v-model:open="showDeleteFolderDialog">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('deleteFolderConfirm') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('deleteFolderWarning') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel
          data-cy="cancel-delete-folder-button"
          @click="showDeleteFolderDialog = false"
        >
          {{ t('cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          class="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          data-cy="confirm-delete-folder-button"
          @click="confirmDeleteFolder"
        >
          {{ t('delete') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <!-- Delete Chat Confirmation Dialog -->
  <AlertDialog v-model:open="showDeleteChatDialog">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('deleteChatConfirm') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('deleteChatWarning') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel
          data-cy="cancel-delete-chat-button"
          @click="showDeleteChatDialog = false"
        >
          {{ t('cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          class="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          data-cy="confirm-delete-chat-button"
          @click="confirmDeleteChat"
        >
          {{ t('delete') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped>
  /* Custom scrollbar styling for sidebar */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f3f4f6;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 9999px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
</style>
